/** SUNTRONIX ONE — Customer PO Intake Workspace.
 *
 * Route:  /commercial/customer-po
 * Purpose: capture, review, and manage Purchase Orders received FROM
 * customers.  Separate from Vendor / Procurement POs.
 *
 * Actions supported:
 *   • Add Customer PO
 *   • Upload Customer PO PDF
 *   • Link to Quotation
 *   • View
 *   • Edit draft (any pre-terminal PO)
 *   • Cancel
 *   • View history
 */
import { useCallback, useEffect, useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  ReceiptText, Plus, RefreshCw, ChevronLeft, Search, Upload, Link2,
  X, Clock, FileText, ArrowRight, ShieldCheck, ThumbsUp, CheckCircle2,
  AlertTriangle, Trash2,
} from "lucide-react";

const fmtINR = (n) => n == null ? "—" :
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR",
    maximumFractionDigits: 0 }).format(n);

const STATUS_TONE = {
  RECEIVED:             "bg-blue-100 text-blue-800 border-blue-200",
  VALIDATION_PENDING: "bg-amber-100 text-amber-800 border-amber-200",
  MATCHED:              "bg-emerald-100 text-emerald-800 border-emerald-200",
  PARTIAL_MATCH:     "bg-amber-100 text-amber-800 border-amber-200",
  MISMATCH:             "bg-rose-100 text-rose-800 border-rose-200",
  REVIEW_REQUIRED: "bg-violet-100 text-violet-800 border-violet-200",
  ACCEPTED:             "bg-emerald-100 text-emerald-800 border-emerald-200",
  REJECTED:             "bg-rose-100 text-rose-800 border-rose-200",
  CANCELLED:          "bg-slate-100 text-slate-600 border-slate-200",
};

export default function CustomerPOIntakePage() {
  const [rows, setRows] = useState([]);
  const [rollup, setRollup] = useState(null);
  const [busy, setBusy] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [selected, setSelected] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Global "Quick Create Customer PO" deep-link — ?new=1[&status=STATUS][&customer_id=CID]
  useEffect(() => {
    let touched = false;
    if (searchParams.get("new") === "1") {
      setShowNew(true);
      searchParams.delete("new");
      touched = true;
    }
    const st = searchParams.get("status");
    if (st) { setStatusFilter(st); searchParams.delete("status"); touched = true; }
    if (touched) setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams]);
  const listCustomerId = searchParams.get("customer_id");

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (search) params.set("q", search);
      const [list, r] = await Promise.all([
        api.get(`/customer-po-intake?${params}`),
        api.get(`/customer-po-intake/rollup`),
      ]);
      setRows((list.data.items || []).filter((x) =>
          !listCustomerId || x.customer_account_id === listCustomerId));
      setRollup(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load"); }
    finally { setBusy(false); }
  }, [statusFilter, search, listCustomerId]);
  useEffect(() => { load(); }, [load]);

  const statuses = rollup?.statuses || [];
  const counts = rollup?.by_status || {};

  return (
    <div className="space-y-4 max-w-[1500px]" data-testid="cpo-intake-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <Link to="/commercial/crm" className="text-[11px] text-emerald-700 hover:underline flex items-center gap-1">
            <ChevronLeft className="h-3 w-3" /> Commercial Workspace
          </Link>
          <h1 className="text-2xl font-heading font-semibold text-slate-900 mt-1 flex items-center gap-2">
            <ReceiptText className="h-6 w-6 text-emerald-700" />
            Customer PO — Intake
          </h1>
          <div className="text-[11px] text-slate-500 mt-1">
            Purchase orders received FROM customers. Separate from Vendor / Procurement POs.
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} disabled={busy} data-testid="cpo-refresh">
            <RefreshCw className={"h-4 w-4 mr-1.5 " + (busy ? "animate-spin" : "")} /> Refresh
          </Button>
          <Button onClick={() => setShowNew(true)}
            className="bg-emerald-700 hover:bg-emerald-800 text-white"
            data-testid="cpo-add">
            <Plus className="h-4 w-4 mr-1.5" /> Add Customer PO
          </Button>
        </div>
      </div>

      {/* Rollup tiles */}
      <div className="grid grid-cols-3 md:grid-cols-6 lg:grid-cols-9 gap-2" data-testid="cpo-rollup">
        <Tile label="Total" value={rollup?.total ?? 0} />
        {statuses.map((s) => (
          <Tile key={s} label={s.replace("_", " ")}
            value={counts[s]?.count || 0}
            tone={STATUS_TONE[s]?.includes("emerald") ? "ok"
                    : STATUS_TONE[s]?.includes("rose")     ? "err"
                    : STATUS_TONE[s]?.includes("amber")   ? "warn"
                    : STATUS_TONE[s]?.includes("violet") ? "info"
                    : undefined} />
        ))}
      </div>

      {/* Search + status chips */}
      <div className="flex gap-2 items-center flex-wrap">
        <div className="relative flex-1 min-w-[240px] max-w-[420px]">
          <Search className="h-4 w-4 absolute left-2 top-2.5 text-slate-400" />
          <Input placeholder="Search code / PO # / customer / reference…"
            className="pl-8 h-9" value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="cpo-search" />
        </div>
        <div className="flex gap-1.5 flex-wrap">
          <Chip active={statusFilter === "all"}
            onClick={() => setStatusFilter("all")} label="All" />
          {statuses.map((s) => (
            <Chip key={s} active={statusFilter === s}
              onClick={() => setStatusFilter(s)}
              label={`${s.replace("_", " ")}`}
              tid={`cpo-filter-${s}`} />
          ))}
        </div>
      </div>

      {/* Rows */}
      <div className="bg-white border border-slate-200 rounded-md divide-y">
        {rows.length === 0 && (
          <div className="p-10 text-center text-[13px] text-slate-500">
            No customer POs {statusFilter !== "all" && `with status ${statusFilter}`} yet.
          </div>
        )}
        {rows.map((r) => (
          <button key={r.id} onClick={() => setSelected(r.id)}
            data-testid={`cpo-row-${r.id}`}
            className="w-full text-left p-4 flex items-center gap-4 hover:bg-slate-50">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-[13px] font-semibold">{r.code}</span>
                <Badge className={"text-[10px] border " + (STATUS_TONE[r.status] || "")}>
                  {r.status?.replace("_", " ")}
                </Badge>
                <span className="text-[11px] text-slate-500">
                  Customer PO # {r.customer_po_number || "—"}
                </span>
              </div>
              <div className="text-[12px] text-slate-700 mt-1 truncate">
                {r.customer_name || "—"}
                {r.customer_code && <span className="text-slate-500 ml-2">({r.customer_code})</span>}
                {r.quotation_code && <span className="text-slate-500 ml-2">· QUO {r.quotation_code}</span>}
                {r.quotation_vtiger_quote_no && (
                  <span className="text-slate-500 ml-2">· Legacy {r.quotation_vtiger_quote_no}</span>
                )}
              </div>
              <div className="text-[10px] text-slate-500 mt-0.5 flex gap-2 flex-wrap">
                {r.po_date && <span>PO {new Date(r.po_date).toLocaleDateString()}</span>}
                {r.po_received_date && <span>· Recv {new Date(r.po_received_date).toLocaleDateString()}</span>}
                {r.source && <span>· {r.source}</span>}
                {r.attachment_id && <span>· PDF attached</span>}
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase text-slate-500">PO value</div>
              <div className="text-[15px] font-heading font-semibold">
                {fmtINR(r.total_amount_inr ?? r.po_value)}
              </div>
            </div>
          </button>
        ))}
      </div>

      {showNew && (
        <NewCustomerPODrawer onClose={() => setShowNew(false)}
          onCreated={() => { setShowNew(false); load(); }} />
      )}
      {selected && (
        <CustomerPODetailDrawer id={selected}
          onClose={() => setSelected(null)}
          onSaved={() => { setSelected(null); load(); }} />
      )}
    </div>
  );
}

function Tile({ label, value, tone }) {
  const tint = tone === "ok" ? "text-emerald-800 bg-emerald-50 border-emerald-200"
             : tone === "warn" ? "text-amber-800 bg-amber-50 border-amber-200"
             : tone === "err" ? "text-rose-800 bg-rose-50 border-rose-200"
             : tone === "info" ? "text-violet-800 bg-violet-50 border-violet-200"
             : "text-slate-700 bg-slate-50 border-slate-200";
  return (
    <div className={"border rounded p-2 " + tint}>
      <div className="text-[9px] uppercase tracking-wider truncate">{label}</div>
      <div className="text-lg font-heading font-semibold mt-0.5">{value ?? "—"}</div>
    </div>
  );
}

function Chip({ active, onClick, label, tid }) {
  return (
    <button onClick={onClick} data-testid={tid}
      className={"px-2.5 py-1 rounded text-[10px] uppercase tracking-wider border " + (
        active ? "border-emerald-500 bg-emerald-50 text-emerald-800"
              : "border-slate-200 text-slate-600 hover:bg-slate-50"
      )}>{label}</button>
  );
}

function Field({ label, children, req }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
        {label}{req && <span className="text-rose-600"> *</span>}
      </div>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Customer selector — search over crm-customers via the existing endpoint
// ---------------------------------------------------------------------------
function CustomerPicker({ value, onChange, disabled }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    let live = true;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/vtiger/crm-customers?q=${encodeURIComponent(q)}&limit=25`);
        if (live) setRows(data.items || []);
      } catch { /* silent */ }
    }, 200);
    return () => { live = false; clearTimeout(t); };
  }, [q]);
  return (
    <div className="relative">
      {value ? (
        <div className="flex items-center justify-between gap-2 border border-emerald-300 bg-emerald-50 rounded-md p-2"
          data-testid="cpo-customer-chip">
          <div className="min-w-0">
            <div className="text-[12px] font-semibold truncate">{value.account_name || value.customer_name}</div>
            <div className="text-[10px] text-slate-500 font-mono">
              {value.customer_code || value.code || "—"}
              {value.vtiger_id && ` · Legacy ${value.vtiger_id}`}
              {value.gstin && ` · ${value.gstin}`}
            </div>
          </div>
          {!disabled && (
            <button className="text-[10px] text-emerald-700 hover:underline"
              onClick={() => onChange(null)}>Change</button>
          )}
        </div>
      ) : (
        <>
          <Input placeholder="Search Customer Master — name / code / GSTIN / city…"
            value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            data-testid="cpo-customer-search" />
          {open && rows.length > 0 && (
            <div className="absolute z-10 top-full left-0 right-0 mt-1 border border-slate-200 bg-white rounded-md shadow max-h-72 overflow-auto">
              {rows.map((r) => (
                <button key={r.id} type="button"
                  onClick={() => { onChange(r); setOpen(false); }}
                  className="w-full text-left px-3 py-2 hover:bg-emerald-50 border-b last:border-b-0 border-slate-100"
                  data-testid={`cpo-customer-opt-${r.id}`}>
                  <div className="text-[12px] font-semibold">{r.account_name}</div>
                  <div className="text-[10px] text-slate-500 font-mono">
                    {r.customer_code || r.code}
                    {r.vtiger_id && ` · Legacy ${r.vtiger_id}`}
                    {r.gstin && ` · ${r.gstin}`}
                    {r.billing_city && ` · ${r.billing_city}`}
                  </div>
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Related quote picker — customer-scoped
// ---------------------------------------------------------------------------
function QuotePicker({ customerId, value, onChange, disabled }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!customerId) { setRows([]); return; }
    setLoading(true);
    api.get(`/customer-po-intake/quotes-for-customer/${customerId}`)
      .then(({ data }) => setRows(data.items || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [customerId]);
  if (!customerId) {
    return <div className="text-[11px] text-slate-500 italic">Select a customer first.</div>;
  }
  return (
    <select value={value || ""} onChange={(e) => onChange(e.target.value || null)}
      disabled={disabled || loading}
      className="w-full h-9 border border-slate-300 rounded-md text-[13px] px-2"
      data-testid="cpo-quote-select">
      <option value="">— No linked quote —</option>
      {rows.map((q) => (
        <option key={q.id} value={q.id}>
          {q.code} · {q.subject || "—"} · {fmtINR(q.grand_total_inr)}
          {q.vtiger_quote_no ? ` · Legacy ${q.vtiger_quote_no}` : ""}
        </option>
      ))}
      {rows.length === 0 && !loading && (
        <option disabled>No quotations found for this customer</option>
      )}
    </select>
  );
}

function LineItemsEditor({ items, onChange, disabled }) {
  const add = () => onChange([...(items || []), {
    product_code: "", product_name: "", quantity: 1,
    unit_price_inr: 0, discount_pct: 0, tax_pct: 18,
  }]);
  const upd = (i, field, val) => {
    const next = [...items];
    next[i] = { ...next[i], [field]: val };
    onChange(next);
  };
  const del = (i) => onChange(items.filter((_, j) => j !== i));
  const total = (it) => {
    const qty = Number(it.quantity) || 0;
    const price = Number(it.unit_price_inr) || 0;
    const disc = Number(it.discount_pct) || 0;
    const tax = Number(it.tax_pct) || 0;
    const sub = qty * price * (1 - disc / 100);
    return sub * (1 + tax / 100);
  };
  return (
    <div className="border border-slate-200 rounded-md" data-testid="cpo-items-editor">
      <div className="p-2 border-b bg-slate-50 flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-wider text-slate-600">
          Line items ({(items || []).length})
        </div>
        {!disabled && (
          <Button size="sm" variant="outline" onClick={add} data-testid="cpo-item-add">
            <Plus className="h-3.5 w-3.5 mr-1" /> Add line
          </Button>
        )}
      </div>
      {(items || []).length === 0 && (
        <div className="p-4 text-center text-[11px] text-slate-500">
          No line items. Validation against the quotation is limited without line data.
        </div>
      )}
      {(items || []).map((it, i) => (
        <div key={i} className="p-2 border-t border-slate-100 grid grid-cols-12 gap-1.5"
          data-testid={`cpo-item-${i}`}>
          <Input placeholder="Code / Part #" value={it.product_code || ""}
            onChange={(e) => upd(i, "product_code", e.target.value)}
            className="col-span-2 h-8 text-[11px]"
            data-testid={`cpo-item-code-${i}`} disabled={disabled} />
          <Input placeholder="Description *" value={it.product_name || ""}
            onChange={(e) => upd(i, "product_name", e.target.value)}
            className="col-span-3 h-8 text-[11px]"
            data-testid={`cpo-item-name-${i}`} disabled={disabled} />
          <Input type="number" placeholder="Qty" value={it.quantity ?? ""}
            onChange={(e) => upd(i, "quantity", e.target.value)}
            className="col-span-1 h-8 text-[11px]"
            data-testid={`cpo-item-qty-${i}`} disabled={disabled} />
          <Input type="number" placeholder="Unit ₹" value={it.unit_price_inr ?? ""}
            onChange={(e) => upd(i, "unit_price_inr", e.target.value)}
            className="col-span-2 h-8 text-[11px]"
            data-testid={`cpo-item-price-${i}`} disabled={disabled} />
          <Input type="number" placeholder="Disc %" value={it.discount_pct ?? ""}
            onChange={(e) => upd(i, "discount_pct", e.target.value)}
            className="col-span-1 h-8 text-[11px]"
            data-testid={`cpo-item-disc-${i}`} disabled={disabled} />
          <Input type="number" placeholder="Tax %" value={it.tax_pct ?? ""}
            onChange={(e) => upd(i, "tax_pct", e.target.value)}
            className="col-span-1 h-8 text-[11px]"
            data-testid={`cpo-item-tax-${i}`} disabled={disabled} />
          <div className="col-span-1 h-8 text-[11px] font-mono text-slate-700 flex items-center justify-end pr-1">
            {fmtINR(total(it))}
          </div>
          {!disabled && (
            <button onClick={() => del(i)}
              className="col-span-1 flex items-center justify-center text-rose-600 hover:bg-rose-50 rounded"
              data-testid={`cpo-item-del-${i}`}>
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      ))}
    </div>
  );
}


// ---------------------------------------------------------------------------
// New Customer PO drawer
// ---------------------------------------------------------------------------
function NewCustomerPODrawer({ onClose, onCreated }) {
  const [customer, setCustomer] = useState(null);
  const [quotationId, setQuotationId] = useState("");
  const [form, setForm] = useState({
    customer_po_number: "",
    customer_reference: "",
    po_date: new Date().toISOString().slice(0, 10),
    po_received_date: new Date().toISOString().slice(0, 10),
    source: "EMAIL",
    currency: "INR",
    po_value: 0,
    tax: 0,
    remarks: "",
  });
  const [pdfB64, setPdfB64] = useState(null);
  const [pdfName, setPdfName] = useState(null);
  const [busy, setBusy] = useState(false);

  const total = useMemo(() =>
    (Number(form.po_value) || 0) + (Number(form.tax) || 0), [form]);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5_000_000) { toast.error("PO PDF must be <= 5 MB"); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const b64 = String(reader.result || "").split(",")[1] || "";
      setPdfB64(b64); setPdfName(f.name);
    };
    reader.readAsDataURL(f);
  };

  const submit = async () => {
    if (!customer) { toast.error("Select a customer"); return; }
    if (!form.customer_po_number) { toast.error("Customer PO number is required"); return; }
    if (!form.po_date) { toast.error("PO date is required"); return; }
    if (!(Number(form.po_value) > 0)) { toast.error("PO value must be > 0"); return; }
    setBusy(true);
    try {
      const payload = {
        customer_account_id: customer.id,
        quotation_id: quotationId || null,
        customer_po_number: form.customer_po_number,
        customer_reference: form.customer_reference || null,
        po_date: form.po_date,
        po_received_date: form.po_received_date || null,
        source: form.source,
        currency: form.currency,
        po_value: Number(form.po_value),
        tax: Number(form.tax) || 0,
        total_amount_inr: total,
        remarks: form.remarks || null,
        po_pdf_b64: pdfB64 || null,
        po_filename: pdfName || null,
      };
      const { data } = await api.post("/customer-po-intake", payload);
      toast.success(`Customer PO ${data.code} recorded`);
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Create failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={onClose}
      data-testid="cpo-new-drawer">
      <div onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl bg-white h-full overflow-auto p-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-heading font-semibold">Add Customer PO</h2>
          <button onClick={onClose} className="text-slate-500 text-[13px]">Close ✕</button>
        </div>
        <div className="mt-5 space-y-4">
          <Field label="Customer (Customer Master)" req>
            <CustomerPicker value={customer} onChange={setCustomer} />
          </Field>
          <Field label="Related SUNTRONIX ONE Quote">
            <QuotePicker customerId={customer?.id} value={quotationId}
              onChange={setQuotationId} />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Customer PO Number" req>
              <Input value={form.customer_po_number}
                onChange={(e) => setForm({ ...form, customer_po_number: e.target.value })}
                data-testid="cpo-po-number" />
            </Field>
            <Field label="Customer Reference">
              <Input value={form.customer_reference}
                onChange={(e) => setForm({ ...form, customer_reference: e.target.value })}
                data-testid="cpo-customer-ref" />
            </Field>
            <Field label="PO Date" req>
              <Input type="date" value={form.po_date}
                onChange={(e) => setForm({ ...form, po_date: e.target.value })}
                data-testid="cpo-po-date" />
            </Field>
            <Field label="PO Received Date">
              <Input type="date" value={form.po_received_date}
                onChange={(e) => setForm({ ...form, po_received_date: e.target.value })}
                data-testid="cpo-received-date" />
            </Field>
            <Field label="Source">
              <select value={form.source} className="w-full h-9 border border-slate-300 rounded-md text-[13px] px-2"
                onChange={(e) => setForm({ ...form, source: e.target.value })}
                data-testid="cpo-source">
                {["EMAIL", "PORTAL", "IN_PERSON", "COURIER", "SYSTEM", "OTHER"].map((s) =>
                  <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="Currency">
              <Input value={form.currency}
                onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })}
                data-testid="cpo-currency" />
            </Field>
            <Field label="PO Value" req>
              <Input type="number" value={form.po_value}
                onChange={(e) => setForm({ ...form, po_value: e.target.value })}
                data-testid="cpo-po-value" />
            </Field>
            <Field label="Tax">
              <Input type="number" value={form.tax}
                onChange={(e) => setForm({ ...form, tax: e.target.value })}
                data-testid="cpo-tax" />
            </Field>
          </div>

          <div className="text-[12px] text-slate-600 bg-slate-50 border border-slate-200 rounded p-2">
            Total (PO Value + Tax): <span className="font-semibold font-mono">{fmtINR(total)}</span>
          </div>

          <div className="text-[11px] text-slate-500 bg-blue-50 border border-blue-100 rounded p-2" data-testid="cpo-inherit-note">
            Payment &amp; delivery terms, addresses and line items are inherited from the linked
            quotation — no need to re-enter them. PO-vs-Quote validation runs automatically against
            the quote's line snapshot.
          </div>

          <Field label="Remarks (internal, optional)">
            <Textarea rows={2} value={form.remarks}
              onChange={(e) => setForm({ ...form, remarks: e.target.value })}
              data-testid="cpo-remarks" />
          </Field>

          <Field label="Upload Customer PO PDF (optional, ≤ 5 MB)">
            <label className="border border-dashed border-slate-300 rounded-md p-3 flex items-center gap-2 cursor-pointer hover:bg-slate-50">
              <Upload className="h-4 w-4 text-slate-500" />
              <span className="text-[12px] text-slate-700">
                {pdfName || "Click to attach the PO PDF"}
              </span>
              <input type="file" accept="application/pdf" onChange={onFile}
                className="hidden" data-testid="cpo-pdf-input" />
            </label>
          </Field>
        </div>

        <div className="mt-6 flex gap-2 sticky bottom-0 bg-white pt-3 border-t border-slate-100">
          <Button variant="outline" onClick={onClose} className="flex-1 h-10">Cancel</Button>
          <Button onClick={submit} disabled={busy}
            className="flex-1 h-10 bg-emerald-700 hover:bg-emerald-800 text-white"
            data-testid="cpo-submit">
            {busy ? "Recording…" : "Record Customer PO"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Detail drawer (view / edit / cancel / link / upload / history)
// ---------------------------------------------------------------------------
function ValidationTab({ cpo, onReload }) {
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showApprove, setShowApprove] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/customer-po-intake/${cpo.id}/validation`);
      setResult(data);
    } catch { setResult(null); }
  }, [cpo.id]);
  useEffect(() => { load(); }, [load]);

  const run = async () => {
    if (!cpo.quotation_id) {
      toast.error("Link this PO to a SUNTRONIX ONE quotation before validating.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/customer-po-intake/${cpo.id}/validate`);
      setResult(data);
      toast.success(`Validation: ${data.overall_status.replace("_", " ")}`);
      onReload();
    } catch (e) { toast.error(e.response?.data?.detail || "Validation failed"); }
    finally { setBusy(false); }
  };

  const st = result?.overall_status;
  const tone = OVERALL_TONE[st] || "bg-slate-100 text-slate-700 border-slate-200";
  const totals = result?.totals || {};
  const needsApproval = st && ["MISMATCH", "REVIEW_REQUIRED"].includes(st);
  const isTerminal = ["ACCEPTED", "REJECTED", "CANCELLED"].includes(cpo.status);

  return (
    <div className="mt-4 space-y-4" data-testid="cpo-validation-tab">
      {!cpo.quotation_id && (
        <div className="p-3 border border-amber-200 bg-amber-50 rounded text-[12px] text-amber-800">
          Link this PO to a SUNTRONIX ONE quotation to enable validation.
        </div>
      )}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500">
            Latest validation
          </div>
          {result ? (
            <div className="flex items-center gap-2 mt-1">
              <Badge className={"text-[11px] border " + tone}
                data-testid="cpo-validation-overall">
                {st.replace("_", " ")}
              </Badge>
              <span className="text-[11px] text-slate-500 font-mono">
                {new Date(result.run_at).toLocaleString()} · {result.run_by}
              </span>
            </div>
          ) : (
            <div className="text-[12px] text-slate-500 italic mt-1">Not run yet.</div>
          )}
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={run} disabled={busy || !cpo.quotation_id || isTerminal}
            className="bg-emerald-700 hover:bg-emerald-800 text-white"
            data-testid="cpo-validate-run">
            <ShieldCheck className="h-3.5 w-3.5 mr-1" />
            {busy ? "Running…" : result ? "Re-run validation" : "Run validation"}
          </Button>
          {needsApproval && !isTerminal && (
            <Button size="sm" onClick={() => setShowApprove(true)}
              className="bg-violet-700 hover:bg-violet-800 text-white"
              data-testid="cpo-approve-mismatch">
              <ThumbsUp className="h-3.5 w-3.5 mr-1" /> Approve Mismatch
            </Button>
          )}
        </div>
      </div>

      {cpo.mismatch_approved && (
        <div className="p-2 border border-violet-200 bg-violet-50 rounded text-[11px]"
          data-testid="cpo-mismatch-badge">
          <div className="font-semibold text-violet-900">
            Mismatch approved · {cpo.mismatch_approved_by}
          </div>
          <div className="text-violet-800 mt-0.5">
            {cpo.mismatch_approved_at && new Date(cpo.mismatch_approved_at).toLocaleString()}
            {cpo.mismatch_approval_notes && ` — "${cpo.mismatch_approval_notes}"`}
          </div>
        </div>
      )}

      {result && (
        <>
          {/* Totals band */}
          <div className="grid grid-cols-3 gap-3">
            <TotalTile label="Quoted total" value={fmtINR(totals.quoted_total)} />
            <TotalTile label="Customer PO total" value={fmtINR(totals.customer_po_total)} />
            <TotalTile label="Total variance"
              value={`${fmtINR(totals.total_variance)} (${totals.total_variance_pct}%)`}
              tone={Math.abs(totals.total_variance_pct || 0) > 2 ? "err"
                       : Math.abs(totals.total_variance_pct || 0) > 0.5 ? "warn" : "ok"} />
          </div>

          {/* Flags chips */}
          <div className="flex flex-wrap gap-1.5" data-testid="cpo-validation-flags">
            {Object.entries(result.flags || {}).map(([k, v]) => (
              <Badge key={k} className={"text-[10px] border " + (v
                ? "bg-rose-50 text-rose-800 border-rose-200"
                : "bg-emerald-50 text-emerald-800 border-emerald-200")}>
                {v ? "●" : "○"} {k.replace(/_/g, " ")}
              </Badge>
            ))}
          </div>

          {/* Header comparison */}
          <ComparisonTable title="Header fields" rows={result.header_checks} />

          {/* Missing / Additional products */}
          {(result.missing_products?.length > 0 || result.additional_products?.length > 0) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <ProductList title="Missing from PO"
                items={result.missing_products}
                emptyLabel="None"
                tone="rose"
                tid="cpo-missing-products" />
              <ProductList title="Additional on PO"
                items={result.additional_products}
                emptyLabel="None"
                tone="amber"
                tid="cpo-additional-products" />
            </div>
          )}

          {/* Line matrix */}
          {result.line_matrix?.length > 0 && (
            <ComparisonTable title="Line-by-line" rows={result.line_matrix} />
          )}
        </>
      )}
      {showApprove && (
        <ApproveMismatchModal cpo={cpo}
          onClose={() => setShowApprove(false)}
          onDone={() => { setShowApprove(false); load(); onReload(); }} />
      )}
    </div>
  );
}

const OVERALL_TONE = {
  EXACT_MATCH:                    "bg-emerald-100 text-emerald-800 border-emerald-200",
  ACCEPTABLE_VARIANCE: "bg-emerald-50 text-emerald-700 border-emerald-200",
  PARTIAL_MATCH:                 "bg-amber-100 text-amber-800 border-amber-200",
  MISMATCH:                             "bg-rose-100 text-rose-800 border-rose-200",
  REVIEW_REQUIRED:              "bg-violet-100 text-violet-800 border-violet-200",
};
const CELL_TONE = OVERALL_TONE;

function TotalTile({ label, value, tone }) {
  const tint = tone === "ok" ? "text-emerald-800 bg-emerald-50 border-emerald-200"
             : tone === "warn" ? "text-amber-800 bg-amber-50 border-amber-200"
             : tone === "err" ? "text-rose-800 bg-rose-50 border-rose-200"
             : "text-slate-700 bg-white border-slate-200";
  return (
    <div className={"border rounded p-2 " + tint}>
      <div className="text-[10px] uppercase tracking-wider">{label}</div>
      <div className="text-lg font-heading font-semibold mt-0.5 font-mono">{value}</div>
    </div>
  );
}

function ComparisonTable({ title, rows }) {
  const fmt = (v, kind) => {
    if (v === null || v === undefined || v === "") return "—";
    if (kind === "money") return fmtINR(v);
    if (typeof v === "string" && v.length > 60) return v.slice(0, 60) + "…";
    return String(v);
  };
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
        {title}
      </div>
      <div className="border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-[11px]">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left px-2 py-1.5">Field</th>
              <th className="text-left px-2 py-1.5">Quote value</th>
              <th className="text-left px-2 py-1.5">PO value</th>
              <th className="text-right px-2 py-1.5">Variance</th>
              <th className="text-center px-2 py-1.5">Match</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-slate-100"
                data-testid={`cpo-row-${r.key || i}`}>
                <td className="px-2 py-1.5 font-medium text-slate-800">{r.label}</td>
                <td className="px-2 py-1.5 text-slate-700 font-mono">{fmt(r.quote_value, r.kind)}</td>
                <td className="px-2 py-1.5 text-slate-700 font-mono">{fmt(r.po_value, r.kind)}</td>
                <td className="px-2 py-1.5 text-right text-slate-700 font-mono">
                  {r.variance != null && r.variance !== "" ? `${r.variance}%` : "—"}
                </td>
                <td className="px-2 py-1.5 text-center">
                  <Badge className={"text-[9px] border " + (CELL_TONE[r.match_status] || "")}>
                    {r.match_status.replace("_", " ")}
                  </Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ProductList({ title, items, emptyLabel, tone, tid }) {
  const tint = tone === "rose" ? "border-rose-200 bg-rose-50"
                     : tone === "amber" ? "border-amber-200 bg-amber-50"
                     : "border-slate-200 bg-slate-50";
  return (
    <div className={"border rounded " + tint} data-testid={tid}>
      <div className="px-2 py-1 border-b border-slate-200/60 text-[10px] uppercase tracking-wider text-slate-700">
        {title} ({items?.length || 0})
      </div>
      {(!items || items.length === 0) ? (
        <div className="p-3 text-center text-[11px] text-slate-500">{emptyLabel}</div>
      ) : (
        <ul className="text-[11px] p-2 space-y-1">
          {items.map((p, i) => (
            <li key={i} className="flex items-center gap-2">
              <span className="font-mono text-slate-800">{p.product_code || "—"}</span>
              <span className="flex-1 truncate">{p.product_name || "—"}</span>
              <span className="font-mono text-slate-600">qty {p.quantity}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ApproveMismatchModal({ cpo, onClose, onDone }) {
  const [notes, setNotes] = useState("");
  const [forceAccept, setForceAccept] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (notes.trim().length < 3) {
      toast.error("Approval notes must be at least 3 characters");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/customer-po-intake/${cpo.id}/approve-mismatch`,
        { approval_notes: notes, force_accept: forceAccept });
      toast.success("Mismatch approved");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Approval failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4"
      onClick={onClose} data-testid="cpo-approve-modal">
      <div onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg bg-white rounded-md p-6 shadow-xl">
        <div className="text-[10px] uppercase tracking-[0.14em] text-violet-700">
          {cpo.code} · {cpo.customer_po_number}
        </div>
        <h3 className="text-xl font-heading font-semibold mt-1">
          Approve mismatched Customer PO
        </h3>
        <div className="mt-1 text-[11px] text-slate-500">
          Records an authorised sign-off on the commercial differences.
          Never bypasses the validation history.
        </div>
        <div className="mt-4">
          <Field label="Approval notes (min 3 chars)" req>
            <Textarea rows={4} value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. GST 18% vs 12% due to product classification, verbal ok from customer, ok to invoice as PO."
              data-testid="cpo-approve-notes" />
          </Field>
          <label className="mt-3 flex items-center gap-2 text-[12px] text-slate-700">
            <input type="checkbox" checked={forceAccept}
              onChange={(e) => setForceAccept(e.target.checked)}
              data-testid="cpo-approve-force" />
            <span>Also transition PO to <span className="font-semibold">ACCEPTED</span></span>
          </label>
        </div>
        <div className="mt-5 flex gap-2">
          <Button variant="outline" onClick={onClose} className="flex-1 h-10">Cancel</Button>
          <Button onClick={() => submit()} disabled={busy}
            className="flex-1 h-10 bg-violet-700 hover:bg-violet-800 text-white"
            data-testid="cpo-approve-submit">
            <ThumbsUp className="h-4 w-4 mr-1.5" />
            {busy ? "Approving…" : forceAccept ? "Approve & Accept" : "Approve mismatch"}
          </Button>
        </div>
      </div>
    </div>
  );
}


function CustomerPODetailDrawer({ id, onClose, onSaved }) {
  const [cpo, setCpo] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(true);
  const [editing, setEditing] = useState(false);
  const [tab, setTab] = useState("summary");

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [d, h] = await Promise.all([
        api.get(`/customer-po-intake/${id}`),
        api.get(`/customer-po-intake/${id}/history`).catch(() => ({ data: { items: [] } })),
      ]);
      setCpo(d.data);
      setHistory(h.data.items || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); onClose(); }
    finally { setBusy(false); }
  }, [id, onClose]);
  useEffect(() => { load(); }, [load]);

  const isTerminal = cpo && ["ACCEPTED", "REJECTED", "CANCELLED"].includes(cpo.status);
  const canCancel  = cpo && !isTerminal && !cpo.sales_order_id;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={onClose}
      data-testid="cpo-detail-drawer-v2">
      <div onClick={(e) => e.stopPropagation()}
        className="w-full max-w-3xl bg-white h-full overflow-auto p-6">
        {busy && <div className="text-center text-slate-500 text-sm">Loading…</div>}
        {!busy && cpo && (
          <>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">
                  {cpo.code} · {cpo.customer_po_number}
                </div>
                <h2 className="text-2xl font-heading font-semibold mt-1">
                  {cpo.customer_name || "—"}
                </h2>
                <div className="flex gap-2 mt-2 flex-wrap">
                  <Badge className={"text-[10px] border " + (STATUS_TONE[cpo.status] || "")}
                    data-testid="cpo-status-badge">
                    {cpo.status?.replace("_", " ")}
                  </Badge>
                  {cpo.customer_code && (
                    <Badge variant="outline" className="text-[10px]">
                      {cpo.customer_code}
                    </Badge>
                  )}
                  {cpo.quotation_code && (
                    <Badge variant="outline" className="text-[10px]">
                      QUO {cpo.quotation_code}
                    </Badge>
                  )}
                  {cpo.quotation_vtiger_quote_no && (
                    <Badge variant="outline" className="text-[10px]">
                      Legacy {cpo.quotation_vtiger_quote_no}
                    </Badge>
                  )}
                </div>
              </div>
              <button onClick={onClose} className="text-slate-500 text-[13px]">Close ✕</button>
            </div>

            <div className="mt-4 flex gap-2 border-b border-slate-200">
              {["summary", "validation", "history"].map((t) => (
                <button key={t} onClick={() => setTab(t)}
                  className={"px-3 py-2 text-[12px] font-medium " + (
                    tab === t ? "border-b-2 border-emerald-700 text-emerald-800"
                              : "text-slate-500 hover:text-slate-800"
                  )}
                  data-testid={`cpo-tab-${t}`}>
                  {t === "summary" ? "Summary"
                    : t === "validation" ? "Validation"
                    : `History (${history.length})`}
                </button>
              ))}
            </div>

            {tab === "summary" && (editing
              ? <EditForm cpo={cpo} onCancel={() => setEditing(false)}
                    onSaved={() => { setEditing(false); load(); onSaved && onSaved(); }} />
              : <SummaryView cpo={cpo}
                    onEdit={() => setEditing(true)}
                    canEdit={!isTerminal}
                    canCancel={canCancel}
                    onReload={load} />
            )}

            {tab === "validation" && (
              <ValidationTab cpo={cpo} onReload={load} />
            )}

            {tab === "history" && (
              <div className="mt-4 border border-slate-200 rounded-md divide-y">
                {history.length === 0 && (
                  <div className="p-4 text-center text-[12px] text-slate-500">
                    No history yet.
                  </div>
                )}
                {history.map((h) => (
                  <div key={h.id} className="p-2.5 flex items-start gap-2 text-[12px]"
                    data-testid={`cpo-history-${h.id}`}>
                    <Clock className="h-3.5 w-3.5 text-slate-500 mt-0.5" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-mono uppercase tracking-wider text-[10px] text-slate-700">
                          {h.event.replace("_", " ")}
                        </span>
                        <span className="text-[10px] text-slate-500 font-mono">
                          {new Date(h.at).toLocaleString()}
                        </span>
                        <span className="text-[10px] text-slate-500">· {h.actor}</span>
                      </div>
                      {h.detail && (
                        <div className="text-[11px] text-slate-600 mt-0.5">{h.detail}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function SummaryView({ cpo, onEdit, canEdit, canCancel, onReload }) {
  const [showLink, setShowLink] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [showCreateOrder, setShowCreateOrder] = useState(false);
  const [showOrderCreated, setShowOrderCreated] = useState(null);

  const cancel = async () => {
    const reason = window.prompt("Reason for cancelling this Customer PO:");
    if (reason === null) return;
    try {
      await api.post(`/customer-po-intake/${cpo.id}/cancel`, { reason });
      toast.success("Customer PO cancelled");
      onReload();
    } catch (e) { toast.error(e.response?.data?.detail || "Cancel failed"); }
  };

  const canCreateOrder = cpo.status === "ACCEPTED" && !cpo.one_order_id;

  return (
    <div className="mt-4 space-y-4">
      {/* ONE Order banner if already created */}
      {cpo.one_order_code && (
        <div className="p-3 border border-emerald-300 bg-emerald-50 rounded flex items-center gap-3"
          data-testid="cpo-order-created-banner">
          <CheckCircle2 className="h-5 w-5 text-emerald-700" />
          <div className="flex-1">
            <div className="text-[13px] font-semibold text-emerald-900">
              ORDER CREATED · <span className="font-mono">{cpo.one_order_code}</span>
            </div>
            <div className="text-[11px] text-emerald-800 mt-0.5">
              Created {cpo.one_order_created_at && new Date(cpo.one_order_created_at).toLocaleString()}
            </div>
          </div>
          <Link to="/commercial/one-orders" className="text-[12px] text-emerald-700 hover:underline">
            View in ONE Orders →
          </Link>
        </div>
      )}

      {/* Create ONE Order CTA when PO is ACCEPTED and no order yet */}
      {canCreateOrder && (
        <div className="p-3 border border-violet-300 bg-violet-50 rounded flex items-center justify-between gap-3"
          data-testid="cpo-create-order-cta">
          <div>
            <div className="text-[13px] font-semibold text-violet-900">
              This PO is ACCEPTED and ready to become an ONE Order
            </div>
            <div className="text-[11px] text-violet-800 mt-0.5">
              Generate the permanent operational order · new STX-ORD-YYYY-NNNNNN
            </div>
          </div>
          <Button size="sm" onClick={() => setShowCreateOrder(true)}
            className="bg-violet-700 hover:bg-violet-800 text-white"
            data-testid="cpo-create-order">
            <FileText className="h-3.5 w-3.5 mr-1" /> Create ONE Order
          </Button>
        </div>
      )}
      <div className="grid grid-cols-3 gap-3">
        <Info label="PO value" value={fmtINR(cpo.total_amount_inr ?? cpo.po_value)} bold />
        <Info label="Tax" value={fmtINR(cpo.tax)} />
        <Info label="Currency" value={cpo.currency || "INR"} />
        <Info label="PO date"
          value={cpo.po_date ? new Date(cpo.po_date).toLocaleDateString() : "—"} />
        <Info label="Received date"
          value={cpo.po_received_date ? new Date(cpo.po_received_date).toLocaleDateString() : "—"} />
        <Info label="Source" value={cpo.source} />
        <Info label="Payment terms" value={cpo.payment_terms} />
        <Info label="Delivery terms" value={cpo.delivery_terms} />
        <Info label="Delivery location" value={cpo.delivery_location} />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Billing address</div>
          <div className="text-[12px] whitespace-pre-line border rounded p-2">{cpo.billing_address || "—"}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Shipping address</div>
          <div className="text-[12px] whitespace-pre-line border rounded p-2">{cpo.shipping_address || "—"}</div>
        </div>
      </div>

      {cpo.customer_reference && (
        <Info label="Customer reference" value={cpo.customer_reference} />
      )}
      {cpo.remarks && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Remarks</div>
          <div className="text-[12px] bg-slate-50 rounded p-2">{cpo.remarks}</div>
        </div>
      )}

      <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-200">
        {cpo.attachment_id && (
          <Button size="sm" variant="outline" onClick={() => window.open(
              `${process.env.REACT_APP_BACKEND_URL}/api/customer-po-intake/${cpo.id}/pdf`, "_blank")}
            data-testid="cpo-view-pdf">
            <FileText className="h-3.5 w-3.5 mr-1" /> View PO PDF
          </Button>
        )}
        {canEdit && (
          <>
            <Button size="sm" variant="outline" onClick={onEdit} data-testid="cpo-edit">
              Edit draft
            </Button>
            <Button size="sm" variant="outline" onClick={() => setShowUpload(true)}
              data-testid="cpo-upload">
              <Upload className="h-3.5 w-3.5 mr-1" /> Upload PO PDF
            </Button>
            <Button size="sm" variant="outline" onClick={() => setShowLink(true)}
              data-testid="cpo-link-quote">
              <Link2 className="h-3.5 w-3.5 mr-1" /> Link to quotation
            </Button>
          </>
        )}
        {canCancel && (
          <Button size="sm" variant="outline" onClick={cancel}
            className="border-rose-500 text-rose-800 hover:bg-rose-50"
            data-testid="cpo-cancel">
            <X className="h-3.5 w-3.5 mr-1" /> Cancel PO
          </Button>
        )}
      </div>

      {showLink && (
        <LinkQuoteModal cpo={cpo} onClose={() => setShowLink(false)}
          onDone={() => { setShowLink(false); onReload(); }} />
      )}
      {showUpload && (
        <UploadModal cpo={cpo} onClose={() => setShowUpload(false)}
          onDone={() => { setShowUpload(false); onReload(); }} />
      )}
      {showCreateOrder && (
        <CreateOneOrderModal cpo={cpo}
          onClose={() => setShowCreateOrder(false)}
          onCreated={(order) => {
            setShowCreateOrder(false);
            setShowOrderCreated(order);
            onReload();
          }} />
      )}
      {showOrderCreated && (
        <OrderCreatedDialog order={showOrderCreated}
          onClose={() => setShowOrderCreated(null)} />
      )}
    </div>
  );
}


function CreateOneOrderModal({ cpo, onClose, onCreated }) {
  const [rdd, setRdd] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [waiver, setWaiver] = useState(null);
  const submit = async (linkExisting = false) => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/customer-po-intake/${cpo.id}/create-one-order`,
        { required_delivery_date: rdd || null, delivery_notes: notes || null, link_existing: linkExisting });
      if (data.linked_existing) toast.success(`PO linked to existing order ${data.code} — waiver closed, no second order raised`);
      onCreated(data);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.code === "WAIVER_ORDER_EXISTS") setWaiver(d);
      else toast.error(typeof d === "string" ? d : d?.message || "Create failed");
    }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4"
      onClick={onClose} data-testid="cpo-create-order-modal">
      <div onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg bg-white rounded-md p-6 shadow-xl">
        <div className="text-[10px] uppercase tracking-[0.14em] text-violet-700">
          {cpo.code} · {cpo.customer_po_number}
        </div>
        <h3 className="text-xl font-heading font-semibold mt-1">
          Create ONE Order
        </h3>
        <div className="mt-1 text-[11px] text-slate-500">
          Generates a permanent SUNTRONIX ONE Order (STX-ORD-YYYY-NNNNNN)
          from this PO and its linked quotation. Vendor procurement is NOT
          started automatically.
        </div>
        <div className="mt-4 space-y-3">
          <Field label="Required Delivery Date">
            <Input type="date" value={rdd} onChange={(e) => setRdd(e.target.value)}
              data-testid="one-order-rdd" />
          </Field>
          <Field label="Delivery Notes">
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
              data-testid="one-order-notes" />
          </Field>
        </div>
        {waiver && (
          <div className="mt-4 rounded-md border border-amber-300 bg-amber-50 p-3 text-[12px] text-amber-900" data-testid="one-order-waiver-guard">
            <div className="font-semibold">Order already exists for this quote</div>
            <div className="mt-1">{waiver.message}</div>
            <Button onClick={() => submit(true)} disabled={busy} className="mt-2 h-8 bg-amber-700 hover:bg-amber-800 text-white" data-testid="one-order-link-existing">
              {busy ? "Linking…" : `Link PO to ${waiver.existing_order?.code}`}
            </Button>
          </div>
        )}
        <div className="mt-5 flex gap-2">
          <Button variant="outline" onClick={onClose} className="flex-1 h-10">Cancel</Button>
          <Button onClick={() => submit(false)} disabled={busy || !!waiver}
            className="flex-1 h-10 bg-violet-700 hover:bg-violet-800 text-white"
            data-testid="one-order-submit">
            <FileText className="h-4 w-4 mr-1.5" />
            {busy ? "Creating…" : "Create ONE Order"}
          </Button>
        </div>
      </div>
    </div>
  );
}


function OrderCreatedDialog({ order, onClose }) {
  return (
    <div className="fixed inset-0 z-[70] bg-black/60 flex items-center justify-center p-4"
      onClick={onClose} data-testid="order-created-dialog">
      <div onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-white rounded-lg p-6 shadow-2xl text-center">
        <CheckCircle2 className="h-14 w-14 text-emerald-600 mx-auto" />
        <div className="mt-3 text-[10px] uppercase tracking-[0.16em] text-emerald-700">
          ORDER CREATED
        </div>
        <div className="mt-2 text-2xl font-heading font-semibold text-slate-900 font-mono"
          data-testid="order-created-code">
          {order.code}
        </div>
        <div className="mt-1 text-[12px] text-slate-500">
          Grand total: <span className="font-mono font-semibold">{fmtINR(order.grand_total_inr)}</span>
        </div>
        <div className="mt-4 flex gap-2">
          <Button variant="outline" onClick={onClose} className="flex-1 h-10">Close</Button>
          <Link to="/commercial/one-orders" className="flex-1">
            <Button className="w-full h-10 bg-emerald-700 hover:bg-emerald-800 text-white">
              View in ONE Orders <ArrowRight className="h-3.5 w-3.5 ml-1" />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}

function Info({ label, value, bold }) {
  return (
    <div className="border rounded p-2">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-[13px] mt-0.5 ${bold ? "font-semibold font-mono" : ""}`}>{value ?? "—"}</div>
    </div>
  );
}

function LinkQuoteModal({ cpo, onClose, onDone }) {
  const [quoteId, setQuoteId] = useState(cpo.quotation_id || "");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!quoteId) { toast.error("Select a quotation"); return; }
    setBusy(true);
    try {
      await api.post(`/customer-po-intake/${cpo.id}/link-quote`, { quotation_id: quoteId });
      toast.success("Quotation linked");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Link failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4"
      onClick={onClose} data-testid="cpo-link-modal">
      <div onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg bg-white rounded-md p-6 shadow-xl">
        <h3 className="text-lg font-heading font-semibold">Link to Quotation</h3>
        <div className="mt-1 text-[11px] text-slate-500">
          Only quotations belonging to <span className="font-semibold">{cpo.customer_name}</span> are listed.
        </div>
        <div className="mt-4">
          <Field label="Related SUNTRONIX ONE Quote" req>
            <QuotePicker customerId={cpo.customer_account_id}
              value={quoteId} onChange={setQuoteId} />
          </Field>
        </div>
        <div className="mt-4 flex gap-2">
          <Button variant="outline" onClick={onClose} className="flex-1 h-10">Cancel</Button>
          <Button onClick={submit} disabled={busy || !quoteId}
            className="flex-1 h-10 bg-emerald-700 hover:bg-emerald-800 text-white"
            data-testid="cpo-link-submit">
            {busy ? "Linking…" : "Link Quotation"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function UploadModal({ cpo, onClose, onDone }) {
  const [b64, setB64] = useState(null);
  const [name, setName] = useState(null);
  const [busy, setBusy] = useState(false);
  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5_000_000) { toast.error("PO PDF must be <= 5 MB"); return; }
    const reader = new FileReader();
    reader.onload = () => {
      setB64(String(reader.result || "").split(",")[1] || "");
      setName(f.name);
    };
    reader.readAsDataURL(f);
  };
  const submit = async () => {
    if (!b64) { toast.error("Select a PDF"); return; }
    setBusy(true);
    try {
      await api.post(`/customer-po-intake/${cpo.id}/upload`,
        { po_pdf_b64: b64, po_filename: name });
      toast.success("PO PDF uploaded");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4"
      onClick={onClose} data-testid="cpo-upload-modal">
      <div onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-white rounded-md p-6 shadow-xl">
        <h3 className="text-lg font-heading font-semibold">Upload Customer PO PDF</h3>
        <div className="mt-4">
          <label className="border border-dashed border-slate-300 rounded-md p-3 flex items-center gap-2 cursor-pointer hover:bg-slate-50">
            <Upload className="h-4 w-4 text-slate-500" />
            <span className="text-[12px] text-slate-700">
              {name || "Click to attach PO PDF (≤ 5 MB)"}
            </span>
            <input type="file" accept="application/pdf" onChange={onFile}
              className="hidden" data-testid="cpo-upload-input" />
          </label>
        </div>
        <div className="mt-4 flex gap-2">
          <Button variant="outline" onClick={onClose} className="flex-1 h-10">Cancel</Button>
          <Button onClick={submit} disabled={busy || !b64}
            className="flex-1 h-10 bg-emerald-700 hover:bg-emerald-800 text-white"
            data-testid="cpo-upload-submit">
            {busy ? "Uploading…" : "Upload"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function EditForm({ cpo, onCancel, onSaved }) {
  const [form, setForm] = useState({
    customer_po_number: cpo.customer_po_number || "",
    customer_reference: cpo.customer_reference || "",
    po_date: (cpo.po_date || "").slice(0, 10),
    po_received_date: (cpo.po_received_date || "").slice(0, 10),
    source: cpo.source || "EMAIL",
    currency: cpo.currency || "INR",
    po_value: cpo.po_value ?? "",
    tax: cpo.tax ?? "",
    payment_terms: cpo.payment_terms || "",
    delivery_terms: cpo.delivery_terms || "",
    delivery_location: cpo.delivery_location || "",
    billing_address: cpo.billing_address || "",
    shipping_address: cpo.shipping_address || "",
    remarks: cpo.remarks || "",
    status: cpo.status,
  });
  const [items] = useState(cpo.items || []);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const payload = Object.fromEntries(
        Object.entries(form).map(([k, v]) => [k, v === "" ? null : v]));
      if (payload.po_value != null) payload.po_value = Number(payload.po_value);
      if (payload.tax != null) payload.tax = Number(payload.tax);
      payload.items = items.map((it) => ({
        product_code: it.product_code || null,
        product_name: it.product_name || "Line item",
        quantity: Number(it.quantity) || 0,
        unit_price_inr: Number(it.unit_price_inr) || 0,
        discount_pct: Number(it.discount_pct) || 0,
        tax_pct: Number(it.tax_pct) || 0,
      }));
      await api.patch(`/customer-po-intake/${cpo.id}`, payload);
      toast.success("Customer PO updated");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-4 space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Customer PO Number" req>
          <Input value={form.customer_po_number}
            onChange={(e) => setForm({ ...form, customer_po_number: e.target.value })} />
        </Field>
        <Field label="Customer Reference">
          <Input value={form.customer_reference}
            onChange={(e) => setForm({ ...form, customer_reference: e.target.value })} />
        </Field>
        <Field label="Status">
          <select value={form.status} className="w-full h-9 border border-slate-300 rounded-md text-[13px] px-2"
            onChange={(e) => setForm({ ...form, status: e.target.value })}
            data-testid="cpo-edit-status">
            {["RECEIVED", "VALIDATION_PENDING", "MATCHED", "PARTIAL_MATCH",
              "MISMATCH", "REVIEW_REQUIRED", "ACCEPTED", "REJECTED"].map((s) =>
                <option key={s} value={s}>{s.replace("_", " ")}</option>)}
          </select>
        </Field>
        <Field label="Source">
          <select value={form.source} className="w-full h-9 border border-slate-300 rounded-md text-[13px] px-2"
            onChange={(e) => setForm({ ...form, source: e.target.value })}>
            {["EMAIL", "PORTAL", "IN_PERSON", "COURIER", "SYSTEM", "OTHER"].map((s) =>
              <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="PO Date"><Input type="date" value={form.po_date}
          onChange={(e) => setForm({ ...form, po_date: e.target.value })} /></Field>
        <Field label="Received Date"><Input type="date" value={form.po_received_date}
          onChange={(e) => setForm({ ...form, po_received_date: e.target.value })} /></Field>
        <Field label="Currency"><Input value={form.currency}
          onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })} /></Field>
        <Field label="PO Value"><Input type="number" value={form.po_value}
          onChange={(e) => setForm({ ...form, po_value: e.target.value })} /></Field>
        <Field label="Tax"><Input type="number" value={form.tax}
          onChange={(e) => setForm({ ...form, tax: e.target.value })} /></Field>
      </div>
      <div className="text-[11px] text-slate-500 bg-blue-50 border border-blue-100 rounded p-2">
        Payment &amp; delivery terms, addresses and line items are inherited from the linked
        quotation and are not edited here. Existing values on older POs are preserved.
      </div>
      <Field label="Remarks"><Textarea rows={2} value={form.remarks}
        onChange={(e) => setForm({ ...form, remarks: e.target.value })} /></Field>

      <div className="flex gap-2 pt-2 border-t border-slate-200 sticky bottom-0 bg-white">
        <Button variant="outline" onClick={onCancel} className="flex-1 h-10">Cancel</Button>
        <Button onClick={submit} disabled={busy}
          className="flex-1 h-10 bg-emerald-700 hover:bg-emerald-800 text-white"
          data-testid="cpo-save">
          {busy ? "Saving…" : "Save changes"}
        </Button>
      </div>
    </div>
  );
}
